import React, { useState, useEffect, useRef } from "react"
import classnames from "classnames"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as metasearch from "metasearch"
import * as uuid from "uuid"
import * as brands from "brands"
import * as api from "./api"
import * as httpx from "httpx"
import * as errors from "errors"
import * as timex from "timex"
import { SearchRequest } from "./brandguard.metrics_pb"
import { Bar } from "charts"
import * as inputs from "inputs"
import * as metricsLayouts from "./layouts"
import * as filters from "./filters"
import { capitalize, formatCategory, INFISO, NEGINFISO } from "./dates"

const eventTypeOptions = ["all", "image", "text", "document", "video"] as const

interface categoryAggregate {
	failureCount: number
	totalScored: number
	failureRate: number
}

interface parsed {
	categories: Map<string, categoryAggregate>
}

function parsedInitial(): parsed {
	return { categories: new Map() }
}

function parse(
	r: api.ScoreFailureMetricSearchResponse,
	uid: string = uuid.NIL,
	eventTypeFilter: string = "all",
): parsed {
	const data = parsedInitial()

	;(r.items || []).forEach((e) => {
		if (uid !== uuid.NIL && e.profile_id !== uid) return
		if (eventTypeFilter !== "all" && e.event_type !== eventTypeFilter) return

		const key = e.score_category
		const failureCount = Number(BigInt(e.failure_count || 0))
		const totalScored = Number(BigInt(e.total_scored || 0))

		const existing = data.categories.get(key) || { failureCount: 0, totalScored: 0, failureRate: 0 }
		existing.failureCount += failureCount
		existing.totalScored += totalScored
		existing.failureRate = existing.totalScored > 0 ? existing.failureCount / existing.totalScored : 0
		data.categories.set(key, existing)
	})

	return data
}

export default function Display(): JSX.Element {
	const brand = brands.caching.useCached()
	const bearertoken = sessions.useToken()

	const [loading, setLoading] = useState(true)
	const [cause, setCause] = useState(undefined as JSX.Element | undefined)

	const [req, setReq] = useState(
		api.SearchZero({
			brand_id: brand.id,
			range: metasearch.dateranges.days(30),
		}) as SearchRequest,
	)

	const [chartData, setChartData] = useState(parsedInitial())
	const [currentUser, setCurrentUser] = useState(filters.zeroUser)
	const [response, setResponse] = useState({} as api.ScoreFailureMetricSearchResponse)
	const [eventTypeFilter, setEventTypeFilter] = useState("all")
	const currentUserRef = useRef(currentUser)
	currentUserRef.current = currentUser
	const eventTypeFilterRef = useRef(eventTypeFilter)
	eventTypeFilterRef.current = eventTypeFilter

	const updateCurrentUser = (user: filters.user): void => {
		setCurrentUser(user)
		setChartData(parse(response, user.key, eventTypeFilter))
	}

	const updateEventTypeFilter = (t: string): void => {
		setEventTypeFilter(t)
		setChartData(parse(response, currentUser.key, t))
	}

	useEffect(() => {
		if (req.brand_id === uuid.NIL || req.range?.oldest === NEGINFISO || req.range?.newest === INFISO) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.search_score_failures(req, bearertoken))
			.then((resp) => {
				setResponse(resp)
				setChartData(parse(resp, currentUserRef.current.key, eventTypeFilterRef.current))
				setCause(undefined)
			})
			.catch(httpx.errors.cancellation(console.warn))
			.catch(
				httpx.errors.forbidden((cause) => {
					console.warn("insufficient privileges unable to view metrics", cause)
					setCause(
						<layouts.containers.box styled m="auto">
							<errors.Textual>you do not have permission to view metrics</errors.Textual>
						</layouts.containers.box>,
					)
				}),
			)
			.catch((c: unknown) => {
				console.error("unable to retrieve metrics", c)
				setCause(
					<layouts.containers.box styled m="auto" width="100%" height="100%">
						<errors.Textual onClick={() => setCause(undefined)}>unable to retrieve metrics</errors.Textual>
					</layouts.containers.box>,
				)
			})
			.finally(() => setLoading(false))
		return p.cancel
	}, [req, bearertoken])

	const sorted = Array.from(chartData.categories.entries()).sort((a, b) => b[1].failureRate - a[1].failureRate)
	const labels = sorted.map(([cat]) => formatCategory(cat))
	const rates = sorted.map(([, agg]) => agg.failureRate * 100)

	const barColors = sorted.map(([, agg]) => {
		if (agg.failureRate >= 0.5) return "rgba(239, 68, 68, 0.7)"
		if (agg.failureRate >= 0.25) return "rgba(248, 205, 68, 0.7)"
		return "rgba(84, 221, 207, 0.7)"
	})

	const borderColors = sorted.map(([, agg]) => {
		if (agg.failureRate >= 0.5) return "#EF4444"
		if (agg.failureRate >= 0.25) return "#F8CD44"
		return "#54ddcf"
	})

	const horizontalOptions = {
		indexAxis: "y",
		maintainAspectRatio: false,
		scales: {
			x: {
				min: 0,
				max: 100,
				ticks: {
					color: layouts.theme.colors.grey.light30,
					callback: (value: number | string) => `${value}%`,
				},
				title: {
					display: true,
					text: "Failure Rate",
					color: layouts.theme.colors.grey.light30,
					font: { size: 14 },
				},
				grid: { display: false },
			},
			y: {
				ticks: {
					color: layouts.theme.colors.grey.light30,
					font: { size: 12 },
				},
				grid: { display: false },
			},
		},
		layout: { padding: 20 },
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					label: (ctx: { raw: unknown; dataIndex: number }) => {
						const val = Number(ctx.raw || 0)
						const cat = sorted[ctx.dataIndex]
						if (!cat) return `${val.toFixed(1)}%`
						return `${val.toFixed(1)}% (${cat[1].failureCount}/${cat[1].totalScored})`
					},
				},
			},
		},
	} as const

	const barData = {
		labels,
		datasets: [
			{
				data: rates,
				backgroundColor: barColors,
				borderColor: borderColors,
				borderWidth: 1,
			},
		],
	}

	const chartHeight = Math.max(300, sorted.length * 40)

	return (
		<layouts.loading.screen loading={loading} height="100%">
			<errors.overlay cause={cause} flexDirection="column" flex="1">
				<layouts.containers.flex>
					<inputs.calendars.DateRangeOneDatepicker
						className={metricsLayouts.StyledDatePicker}
						placeholderText="Date Range"
						minStartDate={new Date(timex.from.iso(req.range?.oldest).minus(timex.duration.days(180)).toISO())}
						maxEndDate={new Date()}
						startDate={new Date(req.range?.oldest || NEGINFISO)}
						endDate={new Date(req.range?.newest || INFISO)}
						changeStart={(d: Date) => {
							setReq((prevState) => ({ ...prevState, range: { newest: INFISO, oldest: timex.from.date(d).toISO() } }))
						}}
						changeEnd={(d: Date) => {
							setReq((prevState) => ({
								...prevState,
								range: { ...prevState.range, newest: timex.from.date(d).toISO() },
							}))
						}}
					/>

					<layouts.containers.flex mt="3px" ml="10px" fontWeight="600">
						{req.range?.oldest !== NEGINFISO &&
							req.range?.newest !== INFISO &&
							`${timex.from
								.iso(req.range?.newest)
								.diff(timex.from.iso(req.range?.oldest), "days")
								.days.toFixed()} day(s)`}
					</layouts.containers.flex>
					<filters.ApprovalFilter
						req={req}
						setReq={setReq}
						currentUser={currentUser}
						setCurrentUser={updateCurrentUser}
					/>
				</layouts.containers.flex>

				<layouts.containers.flex mb="10px" ml="10px">
					{eventTypeOptions.map((t) => (
						<layouts.containers.flex
							key={t}
							px="12px"
							py="4px"
							mr="8px"
							borderRadius="4px"
							cursor="pointer"
							fontSize="13px"
							fontWeight={eventTypeFilter === t ? "700" : "400"}
							color={eventTypeFilter === t ? layouts.theme.colors.white : layouts.theme.colors.grey.dark50}
							background={eventTypeFilter === t ? layouts.theme.colors.blue.blue : layouts.theme.colors.grey.dark50alpha05}
							onClick={() => updateEventTypeFilter(t)}
						>
							{capitalize(t)}
						</layouts.containers.flex>
					))}
				</layouts.containers.flex>

				<layouts.containers.box
					className="metrics"
					width="100%"
					background={layouts.theme.colors.white}
					overflowY="auto"
				>
					<layouts.containers.box
						className={classnames("primary", metricsLayouts.hover)}
						height={`${chartHeight}px`}
						maxHeight="600px"
					>
						<Bar options={horizontalOptions} data={barData} />
					</layouts.containers.box>
				</layouts.containers.box>
			</errors.overlay>
		</layouts.loading.screen>
	)
}
